import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { first, switchMap, map, take } from 'rxjs/operators';

import { Layout } from '../../objects/layout';
import { DateOfStay } from '../../objects/date-of-stay';
import { BaseActionService } from './base.action.service';
import { ApplicationState, LayoutSelectors } from '../reducers';
import { LayoutActions } from './actions';
import { LayoutPosition } from 'src/objects/layout-unit-position';
import { MappedArea } from 'src/objects/mapped-area';
import { Landmark } from 'src/objects/layout-landmark';
import { isNil } from 'lodash-es';
import { Section } from 'src/objects/section';
import { Destination } from 'src/objects/destination';
import { Order } from 'src/objects/order';
import { Product } from 'src/objects/product';

export interface ILayoutActionService {
  loadTemplatedLayoutPositionsWithInventoryUnits(payload: { mappedArea: MappedArea; dateOfStay: DateOfStay}): void;
  loadTemplatedLayoutLandmarks(payload: { mappedArea: MappedArea; dateOfStay: DateOfStay }): void;
  
  selectLayoutUnitPositions(payload: {layoutUnitPositions: LayoutPosition[]}): void;
  selectLayout(payload: {layout: Layout}) : void;

  getLayout(): Observable<Layout| null>;
  getLayoutSelectedUnitPositions(): Observable<LayoutPosition[]| null>;
  getLayoutsValidInventoryUnitIds(): Observable<number[]| null>;
  getLayouts(): Observable<Layout[]| null>;
  getLayoutsLoading(): Observable<boolean>;

  preloadTemplatedLayoutWithLandmarks(payload: { mappedArea: MappedArea; dateOfStay: DateOfStay }): void;
}

@Injectable({
  providedIn: 'root'
})
export class LayoutActionService
  extends BaseActionService<ApplicationState>
  implements ILayoutActionService {
  protected store: Store<ApplicationState>;


  constructor() {
    const store = inject<Store<ApplicationState>>(Store);

    super(store);

    this.store = store;
  }

  readonly layout = this.selectSignal(LayoutSelectors.getLayout);
  readonly layoutError = this.selectSignal(LayoutSelectors.getLayoutError);
  readonly layoutSelectedUnitPositions = this.selectSignal(LayoutSelectors.getLayoutSelectedUnitPositions);
  readonly layoutsValidInventoryUnitIds = this.selectSignal(LayoutSelectors.getLayoutValidPositions);
  readonly layouts = this.selectSignal(LayoutSelectors.getLayouts);
  readonly layoutsLoading = this.selectSignal(LayoutSelectors.getLayoutsLoading);

  loadTemplatedLayoutPositionsWithInventoryUnits(payload: { mappedArea: MappedArea; dateOfStay: DateOfStay }) {
    super.load(LayoutActions.loadTemplatedLayoutPositionsWithInventoryUnits(payload));
  }

  loadTemplatedLayoutLandmarks(payload: { mappedArea: MappedArea; dateOfStay: DateOfStay }) {
    super.load(LayoutActions.loadTemplatedLayoutLandmarks(payload));
  }

  getLayoutError(): Observable<Error | null> {
    return super.get(LayoutSelectors.getLayoutError);
  }

  selectLayoutUnitPositions(payload: {layoutUnitPositions: LayoutPosition[], product: Product, destination: Destination, dateOfStay: DateOfStay, order: Order}) {
    super.load(LayoutActions.selectLayoutUnitPositions(payload));
  }

  selectLayout(payload: {layout: Layout}) {
    super.load(LayoutActions.selectLayout(payload));
  }

  getLayout(): Observable<Layout| null> {
    return super.get(LayoutSelectors.getLayout);
  }

  getLayoutLandmarks(payload: { layout: Layout }): Observable<Landmark[]> {
    if (isNil(payload.layout)) {
      return of([]);
    }
  
    const id = payload.layout.id;
  
    if (isNil(id) || isNaN(+id)) {
      return of([]);
    }
  
    return this.store.select(LayoutSelectors.getLayoutLandmarks)
      .pipe(
        map((byId: (layoutId: number) => Landmark[]) => {
          const landmarks = byId(+id);
  
          return landmarks;
        }),
      );
  }

  getLayoutSelectedUnitPositions(): Observable<LayoutPosition[]| null> {
    return super.get(LayoutSelectors.getLayoutSelectedUnitPositions);
  }

  getLayoutSections(payload: { layout: Layout }): Observable<Section[]> {
    if (isNil(payload.layout)) {
      return of([]);
    }

    const id = payload.layout.id;

    if (isNil(id) || isNaN(+id)) {
      return of([]);
    }

    return this.store.select(LayoutSelectors.getLayoutSections)
      .pipe(
        map((byId: (layoutId: number) => Section[]) => {
          const sections = byId(+id);

          return sections;
        }),
      );
  }

  getLayoutsValidInventoryUnitIds(): Observable<number[]| null> {
    return super.get(LayoutSelectors.getLayoutValidPositions);
  }

  getLayouts(): Observable<Layout[]| null> {
    return super.get(LayoutSelectors.getLayouts);
  }

  getLayoutsLoading(): Observable<boolean> {
    return super.get(LayoutSelectors.getLayoutsLoading);
  }

  preloadTemplatedLayoutWithLandmarks(payload: { 
    mappedArea: MappedArea; 
    dateOfStay: DateOfStay 
  }): void {
    const { mappedArea, dateOfStay } = payload;

    this.getLayout()
        .pipe(
          first(),
          switchMap(layout => {
            if (layout) {
                return this
                  .getLayoutLandmarks({ layout })
                  .pipe(
                    first(),
                    map(landmarks => ({ layout, landmarks }))
                  );
            }

            return of({
              layout: null,
              landmarks: null,
            })
          }),
          take(1),
        )
        .subscribe(({ layout, landmarks }) => {
          if (!layout) {
            this.loadTemplatedLayoutPositionsWithInventoryUnits({ 
              mappedArea, 
              dateOfStay 
            });
          }

          if (!landmarks) {
            this.loadTemplatedLayoutLandmarks({ 
              mappedArea, 
              dateOfStay 
            });
          }
        });
  }
}
